import * as Sentry from '@sentry/nextjs';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { newsSlugs } from '@boss/constants/corporate-jobs';
import { news } from '@boss/services';
import { Environment } from '@boss/types/corporate-jobs';

import SEO from '../../components/SEO';
import { PageTypes } from '../../constants/page';
import { NewsPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { showNewsPages } from '../../utils/options';

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default' || !showNewsPages) {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

  try {
    const [navigation, newsArticles] = await Promise.all([
      getMappedNavigation(contentfulLocaleMarket, !!preview),
      news.getAll(contentfulLocaleMarket ?? 'nl-BE'),
    ]);

    const sortedNewsArticles = newsArticles.sort(function (artikel1, artikel2) {
      return Date.parse(artikel2.publicationDate) - Date.parse(artikel1.publicationDate);
    });

    return {
      props: {
        pageType: PageTypes.NEWS_OVERVIEW,
        preview: !!preview,
        contentfulLocaleMarket,
        environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
        navigation,
        newsArticles: sortedNewsArticles,
        ...(await getServerSideTranslations(locale, ['common', 'news'])),
      },
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        type: 'Fetching news index page',
      },
    });

    return {
      notFound: true,
    };
  }
};

export const Index = ({
  navigation,
  environment,
  preview,
  newsArticles,
  contentfulLocaleMarket,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation(['news']);
  const localizedSlugs = newsSlugs;
  const pageTitle = t('newsTitle');

  return (
    <SEO
      description={pageTitle}
      imageSrc={newsArticles[0]?.image?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow
      noIndex
      title={pageTitle}
    >
      <Layout environment={environment} localizedSlugs={localizedSlugs} navigation={navigation} preview={preview}>
        <NewsPage locale={contentfulLocaleMarket} newsArticles={newsArticles} pageTitle={pageTitle} />
      </Layout>
    </SEO>
  );
};

export default Index;
